import { useCallback, useMemo, useState } from 'react';

import { PlusIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Button } from '@/_components';
import { CardContainer, SectionHeading } from '@/_containers';
import { showFormField } from '@/_shared';

import { AddEditPaymentMethodFieldModal } from './add-edit-payment-method-field-modal';
import { PaymentMethodFieldsTable } from './payment-method-fields-table/payment-method-fields-table';

import type { PaymentMethodFieldFormRowSchema, PaymentMethodFormSchema } from '../schemas';

export function PaymentMethodFieldsSection({ isEditable }: { isEditable: boolean }) {
  const form = useFormContext<PaymentMethodFormSchema>();
  const paymentMethodNumericId = useWatch({ control: form.control, name: 'id' });
  const fieldsRaw = useWatch({ control: form.control, name: 'payment_method_fields' });
  const fields = useMemo(() => fieldsRaw ?? [], [fieldsRaw]);

  const [modalOpen, setModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<PaymentMethodFieldFormRowSchema | null>(null);

  const handleOpenAdd = useCallback(() => {
    setItemToEdit(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((row: PaymentMethodFieldFormRowSchema) => {
    setItemToEdit(row);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setItemToEdit(null);
  }, []);

  const handleApply = useCallback(
    (row: PaymentMethodFieldFormRowSchema) => {
      const rows = form.getValues('payment_method_fields') ?? [];
      if (itemToEdit) {
        const idx = rows.findIndex((r) => r.id === itemToEdit.id);
        if (idx === -1) return;
        const next = [...rows];
        next[idx] = row;
        form.setValue('payment_method_fields', next, { shouldDirty: true });
      } else {
        form.setValue('payment_method_fields', [...rows, row], { shouldDirty: true });
      }
      handleCloseModal();
    },
    [form, itemToEdit, handleCloseModal]
  );

  const handleDelete = useCallback(
    (deleteId: number) => {
      const rows = form.getValues('payment_method_fields') ?? [];
      form.setValue(
        'payment_method_fields',
        rows.filter((r) => r.id !== deleteId),
        { shouldDirty: true }
      );
    },
    [form]
  );

  if (!showFormField(isEditable, fields.length)) {
    return null;
  }

  return (
    <>
      <CardContainer>
        <SectionHeading
          text="Payment Method Fields"
          rightSlot={
            isEditable ? (
              <Button size="xs" onClick={handleOpenAdd} iconLeft={PlusIcon}>
                Add Field
              </Button>
            ) : undefined
          }
        />
        <PaymentMethodFieldsTable
          tableData={fields}
          isEditable={isEditable}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </CardContainer>

      <AddEditPaymentMethodFieldModal
        open={modalOpen}
        onClose={handleCloseModal}
        paymentMethodNumericId={paymentMethodNumericId}
        itemToEdit={itemToEdit}
        onApply={handleApply}
      />
    </>
  );
}
